import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import './App.css';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import SimpleLandingPage from './pages/SimpleLandingPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import StrategiesPage from './pages/StrategiesPage';
import StrategyBuilderPage from './pages/StrategyBuilderPage';
import VisualStrategyBuilder from './pages/VisualStrategyBuilder';
import StrategyModeSelection from './pages/StrategyModeSelection';
import GuidedStrategyBuilder from './pages/GuidedStrategyBuilder';
import StrategyWizard from './pages/StrategyWizard';
import QuickStrategyBuilder from './pages/QuickStrategyBuilder';
import BacktestPage from './pages/BacktestPage';
import BacktestResultPage from './pages/BacktestResultPage';
import SignalsPage from './pages/SignalsPage';
import SimpleSignalsPage from './pages/SimpleSignalsPage';
import SignalHistoryPage from './pages/SignalHistoryPage';
import MarketplacePage from './pages/MarketplacePage';
import BotDetailPage from './pages/BotDetailPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import SettingsPage from './pages/SettingsPage';
import ComingSoonPage from './pages/ComingSoonPage';
import DeprecatedPage from './pages/DeprecatedPage';

// Gelir Modeli Sayfaları
import TraderPage from './pages/TraderPage';
import SellerPage from './pages/SellerPage';
import PartnerPage from './pages/PartnerPage';
import PartnerDashboardPage from './pages/PartnerDashboardPage';
import SubscriberPage from './pages/SubscriberPage';
import PricingPage from './pages/PricingPage';
import PaymentPage from './pages/PaymentPage';
import ReferralWalletPage from './pages/ReferralWalletPage';
import ReferralProgramPage from './pages/ReferralProgramPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AnalystPanel from './pages/AnalystPanel';
import AnalystProfilePage from './pages/AnalystProfilePage';
import LogoShowcase from './pages/LogoShowcase';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import ReferralBanner from './components/ReferralBanner';
import EmailVerificationPage from './pages/EmailVerificationPage';
import TicketsPage from './pages/TicketsPage';
import PerformanceDashboard from './pages/PerformanceDashboard';

// Legal Pages
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsPage from './pages/legal/TermsPage';

// Telegram Landing Page
import TelegramLandingPage from './pages/TelegramLandingPage';
import RiskDisclosurePage from './pages/legal/RiskDisclosurePage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';

// Rehber
import RehberPage from './pages/rehber/RehberPage';
import RehberArticle from './pages/rehber/RehberArticle';
import KriptoSinyaliNedirPage from './pages/rehber/KriptoSinyaliNedirPage';
import TpStopLossNedirPage from './pages/rehber/TpStopLossNedirPage';
import LongShortNedirPage from './pages/rehber/LongShortNedirPage';
import SinyalNasilOkunurPage from './pages/rehber/SinyalNasilOkunurPage';
import RiskYonetimiPage from './pages/rehber/RiskYonetimiPage';
import RSINedirPage from './pages/rehber/RSINedirPage';
import MACDNedirPage from './pages/rehber/MACDNedirPage';
import FibonacciNedirPage from './pages/rehber/FibonacciNedirPage';
import DestekDirencNedirPage from './pages/rehber/DestekDirencNedirPage';
import LongNedirPage from './pages/rehber/LongNedirPage';
import ShortNedirPage from './pages/rehber/ShortNedirPage';
import FuturesTradingNedirPage from './pages/rehber/FuturesTradingNedirPage';

// Kurumsal
import HakkimizdaPage from './pages/kurumsal/HakkimizdaPage';
import IsBirlikleriPage from './pages/kurumsal/IsBirlikleriPage';
import IletisimPage from './pages/kurumsal/IletisimPage';

// SSS
import SSSPage from './pages/SSSPage';

// SEO Landing Pages
import AiKriptoSinyalleriPage from './pages/landing/AiKriptoSinyalleriPage';
import BtcSinyalleriPage from './pages/landing/BtcSinyalleriPage';
import ShortSinyallerPage from './pages/landing/ShortSinyallerPage';
import LongSinyallerPage from './pages/landing/LongSinyallerPage';
import EthSinyalleriPage from './pages/landing/EthSinyalleriPage';
import EnIyiKriptoSinyalleriPage from './pages/landing/EnIyiKriptoSinyalleriPage';
import KriptoSinyalleriPage from './pages/landing/KriptoSinyalleriPage';
import AltcoinSinyalleriPage from './pages/landing/AltcoinSinyalleriPage';

// Admin
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStorage from './pages/admin/AdminStorage';
import AdminBackup from './pages/admin/AdminBackup';
import AdminPayments from './pages/admin/AdminPayments';
import AdminUsers from './pages/admin/AdminUsers';
import AdminLogs from './pages/admin/AdminLogs';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminStrategies from './pages/admin/AdminStrategies';
import AdminMarketplace from './pages/admin/AdminMarketplace';
import AdminSignals from './pages/admin/AdminSignals';
import AdminContact from './pages/admin/AdminContact';
import AdminTickets from './pages/admin/AdminTickets';
import AdminScannerDashboard from './pages/admin/AdminScannerDashboard';
import AdminContrarianDashboard from './pages/admin/AdminContrarianDashboard';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
// OnboardingModal removed - not needed for simple mode
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════════════════
// AXIOS CONFIGURATION - Token Management & Session Stability
// ═══════════════════════════════════════════════════════════════════

// Set axios defaults
axios.defaults.baseURL = API;

// Request interceptor - Add auth token to all requests
axios.interceptors.request.use((config) => {
  // Skip auth for shared backtest endpoints and public endpoints
  const publicPaths = ['/shared/', '/auth/login', '/auth/register'];
  const isPublic = publicPaths.some(path => config.url?.includes(path));
  
  if (!isPublic) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor - Handle 401 errors gracefully & auto-refresh tokens
axios.interceptors.response.use(
  (response) => {
    // Token refresh: Yeni token varsa localStorage'ı güncelle
    const newToken = response.headers['x-new-token'];
    if (newToken) {
      console.log('Token refreshed automatically');
      localStorage.setItem('token', newToken);
    }
    return response;
  },
  (error) => {
    // Don't logout on network errors or non-401 errors
    if (!error.response) {
      console.warn('Network error - keeping session');
      return Promise.reject(error);
    }
    
    const status = error.response?.status;
    const url = error.config?.url || '';
    
    // Only logout on 401 from protected endpoints (not login/register)
    if (status === 401) {
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
      
      if (!isAuthEndpoint) {
        console.warn('Session expired - clearing token');
        // Clear token but don't force redirect - let PrivateRoute handle it
        localStorage.removeItem('token');
        // Dispatch custom event for AuthContext to pick up
        window.dispatchEvent(new Event('auth:logout'));
      }
    }
    
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" state={{ from: location }} replace />;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/signals" />;
}

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLandingPage = location.pathname === '/';
  const isTelegramLanding = ['/telegram', '/kanal', '/canli'].includes(location.pathname);
  
  return (
    <div className="App flex flex-col min-h-screen">
      <ScrollToTop />
      {!isAdminRoute && !isTelegramLanding && <ReferralBanner />}
      {!isAdminRoute && !isTelegramLanding && <Navbar />}
      {!isAdminRoute && !isTelegramLanding && <EmailVerificationBanner />}
      
      <main className="flex-grow">
        <Routes>
        <Route path="/" element={<SimpleLandingPage />} />
        <Route path="/pro" element={<LandingPage />} />
        
        {/* ═══════════════════════════════════════════════════════════════════
            KALDIRILMIŞ SAYFALAR - 410 Gone (Google deindex için)
            Bu sayfalar eski uygulamaya aitti ve artık mevcut değil
        ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/subscriber" element={<DeprecatedPage />} />
        <Route path="/trader" element={<DeprecatedPage />} />
        <Route path="/seller" element={<DeprecatedPage />} />
        <Route path="/partner" element={<DeprecatedPage />} />
        <Route path="/marketplace" element={<DeprecatedPage />} />
        <Route path="/marketplace/*" element={<DeprecatedPage />} />
        <Route path="/strategies" element={<DeprecatedPage />} />
        <Route path="/strategies/*" element={<DeprecatedPage />} />
        <Route path="/strategy-builder" element={<DeprecatedPage />} />
        <Route path="/visual-strategy-builder" element={<DeprecatedPage />} />
        <Route path="/guided-strategy-builder" element={<DeprecatedPage />} />
        <Route path="/strategy-wizard" element={<DeprecatedPage />} />
        <Route path="/quick-strategy" element={<DeprecatedPage />} />
        <Route path="/backtest/*" element={<DeprecatedPage />} />
        <Route path="/analyst" element={<DeprecatedPage />} />
        <Route path="/bots" element={<DeprecatedPage />} />
        <Route path="/trading-bots" element={<DeprecatedPage />} />
        <Route path="/beta" element={<DeprecatedPage />} />
        
        {/* AKTİF SAYFALAR */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/nasil-calisir" element={<HowItWorksPage />} />
        <Route path="/referans-programi" element={<ReferralProgramPage />} />
        <Route path="/r/:code" element={<ReferralLandingPage />} />
        <Route 
          path="/payment" 
          element={
            <PrivateRoute>
              <PaymentPage />
            </PrivateRoute>
          } 
        />
        <Route path="/logo-showcase" element={<LogoShowcase />} />
        
        {/* Legal Pages */}
        <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/risk" element={<RiskDisclosurePage />} />
        <Route path="/legal/cookies" element={<CookiePolicyPage />} />
        
        {/* Telegram Landing Page - Google Ads */}
        <Route path="/telegram" element={<TelegramLandingPage />} />
        <Route path="/kanal" element={<TelegramLandingPage />} />
        <Route path="/canli" element={<TelegramLandingPage />} />
        
        {/* Kurumsal */}
        <Route path="/kurumsal/hakkimizda" element={<HakkimizdaPage />} />
        <Route path="/kurumsal/is-birlikleri" element={<IsBirlikleriPage />} />
        <Route path="/kurumsal/iletisim" element={<IletisimPage />} />
        
        {/* SSS */}
        <Route path="/sss" element={<SSSPage />} />
        
        {/* SEO Landing Pages */}
        <Route path="/ai-kripto-sinyalleri" element={<AiKriptoSinyalleriPage />} />
        <Route path="/btc-sinyalleri" element={<BtcSinyalleriPage />} />
        <Route path="/eth-sinyalleri" element={<EthSinyalleriPage />} />
        <Route path="/short-sinyaller" element={<ShortSinyallerPage />} />
        <Route path="/long-sinyaller" element={<LongSinyallerPage />} />
        <Route path="/en-iyi-kripto-sinyalleri" element={<EnIyiKriptoSinyalleriPage />} />
        <Route path="/kripto-sinyalleri" element={<KriptoSinyalleriPage />} />
        <Route path="/altcoin-sinyalleri" element={<AltcoinSinyalleriPage />} />
        
        {/* Rehber Pages */}
        <Route path="/rehber" element={<RehberPage />} />
        <Route path="/rehber/kripto-sinyali-nedir" element={<KriptoSinyaliNedirPage />} />
        <Route path="/rehber/tp-ve-stop-loss-nedir" element={<TpStopLossNedirPage />} />
        <Route path="/rehber/long-ve-short-ne-demek" element={<LongShortNedirPage />} />
        <Route path="/rehber/kripto-sinyali-nasil-okunur" element={<SinyalNasilOkunurPage />} />
        <Route path="/rehber/yeni-baslayanlar-icin-risk-yonetimi" element={<RiskYonetimiPage />} />
        <Route path="/rehber/rsi-nedir" element={<RSINedirPage />} />
        <Route path="/rehber/macd-nedir" element={<MACDNedirPage />} />
        <Route path="/rehber/fibonacci-nedir" element={<FibonacciNedirPage />} />
        <Route path="/rehber/destek-direnc-nedir" element={<DestekDirencNedirPage />} />
        <Route path="/rehber/long-nedir" element={<LongNedirPage />} />
        <Route path="/rehber/short-nedir" element={<ShortNedirPage />} />
        <Route path="/rehber/futures-trading-nedir" element={<FuturesTradingNedirPage />} />
        
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        
        {/* Password Reset */}
        <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
        <Route path="/sifre-sifirla" element={<ResetPasswordPage />} />
        
        {/* Email Verification */}
        <Route path="/email-dogrulama" element={<EmailVerificationPage />} />
        
        {/* Sinyal Sayfaları */}
        <Route path="/signals" element={<SimpleSignalsPage />} />
        <Route path="/signals/history" element={<SignalHistoryPage />} />
        <Route path="/performans" element={<PerformanceDashboard />} />
        
        {/* Coming Soon - Gelecek özellikler */}
        <Route path="/yakinda/:feature" element={<ComingSoonPage />} />
        <Route path="/dashboard" element={<Navigate to="/signals" replace />} />
        
        {/* Diğer sayfalar */}
        <Route path="/analyst/:analystId" element={<AnalystProfilePage />} />
        <Route path="/shared/backtest/:shareToken" element={<BacktestResultPage />} />
        
        {/* Tickets Route (Pro Only) */}
        <Route
          path="/destek"
          element={
            <PrivateRoute>
              <TicketsPage />
            </PrivateRoute>
          }
        />
        
        {/* Account Routes */}
        <Route
          path="/account/subscriptions"
          element={
            <PrivateRoute>
              <SubscriptionsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/account/settings"
          element={
            <PrivateRoute>
              <SettingsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/account/partner"
          element={
            <PrivateRoute>
              <PartnerDashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/referans"
          element={
            <PrivateRoute>
              <ReferralWalletPage />
            </PrivateRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedAdminRoute>
              <AdminLayout />
            </ProtectedAdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="revenue" element={<AdminRevenue />} />
          <Route path="storage" element={<AdminStorage />} />
          <Route path="backup" element={<AdminBackup />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="strategies" element={<AdminStrategies />} />
          <Route path="marketplace" element={<AdminMarketplace />} />
          <Route path="signals" element={<AdminSignals />} />
          <Route path="scanner" element={<AdminScannerDashboard />} />
          <Route path="contrarian" element={<AdminContrarianDashboard />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="tickets" element={<AdminTickets />} />
        </Route>
      </Routes>
      </main>
      {!isAdminRoute && !isTelegramLanding && <Footer />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
